/**
 * Flattens one candidate's whole record — resume scores plus every screening attempt — into CSV
 * rows.
 *
 * CSV is flat and a candidate is not: they have N call attempts, each with its own rating,
 * transcript and screening_data. So the shape is ONE ROW PER ATTEMPT, with the candidate's fields
 * repeated on each. That denormalisation is deliberate: it's what makes the file usable in a
 * spreadsheet, where a recruiter will filter and sort it. A candidate who was never called still
 * gets exactly one row, so "export" never produces an empty file.
 *
 * screening_data keys are NOT hardcoded — the webhook's schema grows, and a key we've never heard
 * of still has to reach the file. Every key seen across the candidate's attempts becomes a column.
 */

import { exportToCsv } from "../../../utils/exportCsv";
import { getScreeningData, getBestScreeningResult } from "../../../services/screeningService";
import { fieldLabel, formatValue } from "./screeningFields";

/**
 * Two decimals, or an empty cell.
 *
 * A blank is not a zero: a candidate who was never called has no screen score, and writing "0.00"
 * would rank them below someone who genuinely scored 1. Empty is the only honest cell.
 */
export const score2 = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : "";
};

const CANDIDATE_HEADERS = [
  "Name",
  "Email",
  "Phone",
  "Rank",
  "Stage",
  "Total score",
  // Next to the total on purpose: they're the two headline numbers, and a reader comparing them
  // shouldn't have to scroll past eight columns of call metadata to find the second one.
  "Screen score",
  "Skills",
  "Experience",
  "Education",
  "Peer-to-peer",
];

const ATTEMPT_HEADERS = [
  "Attempt",
  "Called at",
  "Call status",
  "Outcome",
  // Deliberately NOT called "Screen score": that column is the candidate's headline (their best
  // completed call). This one is *this* attempt's rating, and two columns with one name in a
  // spreadsheet is how someone filters on the wrong number.
  "Attempt score",
  "Duration (s)",
  "Callback requested",
  "Disqualification reason",
  "Call summary",
  "Transcript",
  "Recording link",
];

/**
 * The screen score is the BEST completed attempt, matching the table and the detail panel — a later
 * 20-second no-answer doesn't erase an 8.6. The export must agree with what the recruiter saw on
 * screen; a file that disagrees with the UI is worse than no file.
 */
const candidateCells = (row, results) => [
  row.name,
  row.email,
  row.phone,
  row.rank,
  row.stage,
  score2(row.total),
  score2(getBestScreeningResult(results)?.interview_rating),
  score2(row.skills),
  score2(row.exp),
  score2(row.edu),
  score2(row.p2p),
];

const attemptCells = (result, index) => [
  index + 1,
  result?.created_at,
  result?.call_status,
  result?.outcome,
  score2(result?.interview_rating),
  result?.call_duration,
  result?.callback_time,
  result?.disqualification_reason,
  result?.call_summary,
  result?.transcript,
  result?.call_recording_link,
];

/** Every screening_data key this candidate's attempts produced, in registry order where known. */
const dataKeysOf = (results) => {
  const seen = new Set();
  results.forEach((r) => Object.keys(r?.screening_data || {}).forEach((k) => seen.add(k)));
  return [...seen];
};

/**
 * `screening_data` is per-attempt, but an attempt that only got a partial answer leaves gaps. The
 * table's cells resolve those gaps through getScreeningData (the best attempt that HAS data), and
 * the export must agree with what the recruiter saw on screen — a file that disagrees with the UI
 * is worse than no file. So the per-attempt column falls back to the same resolved record.
 */
export const candidateCsvRows = (row, results = []) => {
  const keys = dataKeysOf(results);
  const headers = [...CANDIDATE_HEADERS, ...ATTEMPT_HEADERS, ...keys.map(fieldLabel)];
  const resolved = getScreeningData(results) || {};

  const dataCells = (result) =>
    keys.map((key) => {
      const value = result?.screening_data?.[key] ?? resolved[key];
      const text = formatValue(value);
      return text === "—" ? "" : text;
    });

  const rows = results.length
    ? results.map((result, i) => [
        ...candidateCells(row, results),
        ...attemptCells(result, i),
        ...dataCells(result),
      ])
    : [[...candidateCells(row, results), ...ATTEMPT_HEADERS.map(() => ""), ...keys.map(() => "")]];

  return { headers, rows };
};

/** "Asha Rao" -> "asha-rao-screening.csv" */
const filenameFor = (row) => {
  const slug = String(row?.name || "candidate")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `${slug || "candidate"}-screening.csv`;
};

export const exportCandidate = (row, results = []) => {
  const { headers, rows } = candidateCsvRows(row, results);
  exportToCsv(filenameFor(row), headers, rows);
};
